import React from 'react';
import { twMerge } from 'tailwind-merge';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';

/**
 * Shared surface for every control in the row; the off state overrides the fill.
 *
 * Exported because the device-settings cog is part of the same row but cannot
 * use this component — it is a PopoverMenu trigger — and a second copy of these
 * classes would drift the moment either changed.
 */
export const LOBBY_CONTROL_CLASS =
    'flex size-10 items-center justify-center rounded-xl bg-white text-gray-800 hover:bg-gray-200';
const CONTROL_OFF_CLASS = 'bg-red text-white hover:brightness-90';

interface Props {
    iconKey: IconKey;
    onClick: () => void;
    dataTestId: string;
    /**
     * Whether the thing this controls is currently on. Off paints the control
     * red, the way a muted mic reads across the app. Omitted by controls that
     * open something rather than toggling it.
     */
    isOn?: boolean;
    /** A control for a device we were refused; still shown, but inert. */
    disabled?: boolean;
    tooltipLabel?: string;
}

/**
 * One control in the lobby's row over the video preview — the lobby's answer to
 * MeetingButton, so the three buttons share a surface instead of each carrying
 * a copy of it.
 */
const MeetingLobbyDeviceButton = ({
    iconKey,
    onClick,
    dataTestId,
    isOn = true,
    disabled,
    tooltipLabel,
}: Props) => (
    <IconButton
        data-test-id={dataTestId}
        // State for tests to assert against, so they never have to read a
        // colour: the lobby's colour tokens invert in dark mode, so a literal
        // rgb() only ever holds in one theme.
        data-test-state={isOn ? 'on' : 'off'}
        className={twMerge(
            LOBBY_CONTROL_CLASS,
            !isOn && CONTROL_OFF_CLASS,
            disabled && 'cursor-not-allowed',
        )}
        onClick={onClick}
        disabled={disabled}
        tooltipLabel={tooltipLabel}
    >
        <Icon iconKey={iconKey} size="1.5rem" />
    </IconButton>
);

export default MeetingLobbyDeviceButton;
